import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Combobox, ComboboxList, ComboboxProvider } from "@ariakit/react";
import * as RadixPopover from "@radix-ui/react-popover";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { X, Check } from "lucide-react";
import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";

type Props = {
    id?: string;
    disabled?: boolean;
    placeholder?: string;
    options: string[];
    value: string[];
    onChange: (value: string[]) => void;
    error?: string;
    "data-testid"?: string;
} & PropsWithClassName;

export const MultiSelect: React.FC<Props> = ({
    id,
    disabled,
    placeholder = "Select...",
    options,
    value,
    onChange,
    error,
    "data-testid": testId,
    className,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [open, setOpen] = useState(false);
    const [lastSearchValue, setLastSearchValue] = useState("");
    const comboboxRef = useRef<HTMLInputElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);

    // Show all options (including selected ones) in dropdown, filtered by last search
    // Use lastSearchValue to keep the filtered list stable after clearing input
    const filteredOptions = options.filter(
        (item) => !lastSearchValue || item.toLowerCase().includes(lastSearchValue.toLowerCase()),
    );

    const handleSelect = useCallback(
        (selectedValue: string, event: React.MouseEvent) => {
            // Prevent default to stop dropdown from closing
            event.preventDefault();
            event.stopPropagation();

            // Toggle selection
            if (value.includes(selectedValue)) {
                onChange(value.filter((v) => v !== selectedValue));
            } else {
                onChange([...value, selectedValue]);
            }

            // Clear the input but keep the filtered list
            setSearchValue("");

            // Keep dropdown open
            setOpen(true);

            // Refocus input to allow continued typing
            setTimeout(() => {
                comboboxRef.current?.focus();
            }, 0);
        },
        [value, onChange],
    );

    const handleRemove = useCallback(
        (removedValue: string) => {
            onChange(value.filter((v) => v !== removedValue));
        },
        [value, onChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Backspace" && !searchValue && value.length > 0) {
                // Remove last tag on backspace when input is empty
                onChange(value.slice(0, -1));
            }
        },
        [searchValue, value, onChange],
    );

    const handleSearchChange = useCallback((newValue: string) => {
        setSearchValue(newValue);
        setLastSearchValue(newValue);
    }, []);

    const handleFocus = useCallback(() => {
        // Reset filter to show all options when re-focusing
        setLastSearchValue("");
        setOpen(true);
    }, []);

    return (
        <RadixPopover.Root open={open} onOpenChange={setOpen} modal={false}>
            <ComboboxProvider value={searchValue} setValue={handleSearchChange}>
                <RadixPopover.Anchor asChild>
                    <div
                        className={cn(
                            standaloneInputClassName,
                            // Override to allow wrapping and proper vertical centering
                            "!flex !min-h-[36px] !flex-wrap !items-center !py-1.5",
                            // Error states
                            {
                                "border-border-error ring-ring-destructive": error,
                            },
                            className,
                        )}
                        onClick={() => !disabled && comboboxRef.current?.focus()}
                        data-tracking-id="multiselect-container"
                    >
                        {value.map((item) => (
                            <span
                                key={item}
                                className={cn(
                                    "inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-sm font-medium",
                                    "bg-content-brand text-content-primary-inverted",
                                    "transition-colors",
                                    "my-0.5",
                                )}
                            >
                                <span className="select-none">{item}</span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove(item);
                                    }}
                                    disabled={disabled}
                                    className={cn(
                                        "flex items-center justify-center rounded-sm transition-colors",
                                        "hover:bg-black/20 active:bg-black/30",
                                        "dark:hover:bg-white/20 dark:active:bg-white/30",
                                        "disabled:cursor-not-allowed disabled:opacity-50",
                                    )}
                                    aria-label={`Remove ${item}`}
                                    data-tracking-id={`multiselect-remove-${item}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        ))}
                        <Combobox
                            id={id}
                            placeholder={value.length === 0 ? placeholder : ""}
                            className={cn(
                                "min-w-[120px] flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none focus-within:!shadow-none focus-within:!ring-0",
                                "text-content-primary placeholder:text-content-tertiary",
                                "disabled:cursor-not-allowed",
                            )}
                            disabled={disabled}
                            data-testid={testId}
                            ref={comboboxRef}
                            onKeyDown={handleKeyDown}
                            onFocus={handleFocus}
                        />
                    </div>
                </RadixPopover.Anchor>
                {filteredOptions.length > 0 && (
                    <RadixPopover.Portal>
                        <RadixPopover.Content
                            asChild
                            sideOffset={4}
                            align="start"
                            onOpenAutoFocus={(event) => event.preventDefault()}
                            onInteractOutside={(event) => {
                                const target = event.target as Element | null;
                                const isCombobox = target === comboboxRef.current;
                                const inListbox = target && listboxRef.current?.contains(target);
                                if (isCombobox || inListbox) {
                                    event.preventDefault();
                                }
                            }}
                        >
                            <ScrollAreaPrimitive.Root
                                className={cn(
                                    "z-50 rounded-lg border border-border-base shadow-lg",
                                    "bg-surface-popover",
                                )}
                                type="auto"
                            >
                                <ScrollAreaPrimitive.Viewport
                                    className={cn(
                                        "rounded-lg",
                                        "max-h-[min(240px,calc(var(--radix-popper-available-height)-16px))]",
                                        "w-[var(--radix-popover-trigger-width)] min-w-[200px]",
                                    )}
                                >
                                    <ComboboxList
                                        ref={listboxRef}
                                        role="listbox"
                                        className={cn("p-1", "focus:outline-none")}
                                    >
                                        {filteredOptions.map((option) => {
                                            const isSelected = value.includes(option);
                                            return (
                                                <div
                                                    key={option}
                                                    onClick={(e) => handleSelect(option, e)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    className={cn(
                                                        "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-3 py-2 text-base outline-none",
                                                        "text-content-primary",
                                                        "transition-colors",
                                                        "hover:bg-surface-hover",
                                                    )}
                                                    data-tracking-id={`multiselect-option-${option}`}
                                                >
                                                    <span>{option}</span>
                                                    {isSelected && <Check className="h-4 w-4 text-content-brand" />}
                                                </div>
                                            );
                                        })}
                                    </ComboboxList>
                                </ScrollAreaPrimitive.Viewport>
                                <ScrollAreaPrimitive.Scrollbar
                                    className="flex touch-none select-none p-0.5"
                                    orientation="vertical"
                                >
                                    <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-lg bg-border-base" />
                                </ScrollAreaPrimitive.Scrollbar>
                            </ScrollAreaPrimitive.Root>
                        </RadixPopover.Content>
                    </RadixPopover.Portal>
                )}
            </ComboboxProvider>
        </RadixPopover.Root>
    );
};
